import React, { useState, useEffect } from 'react';
import { crmApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Zap, Plus, Edit, Trash2, Loader2, Send, Pause, Play, CheckCircle, Mail, Globe } from 'lucide-react';

const STATUS_CONFIG = {
  draft: { color: '#64748B', label: 'Draft', icon: Mail },
  active: { color: '#166534', label: 'Active', icon: Play },
  paused: { color: '#92400E', label: 'Paused', icon: Pause },
  completed: { color: '#0E7490', label: 'Completed', icon: CheckCircle },
};

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', campaign_type: 'email', status: 'draft', target_audience: '', subject_line: '', content: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await crmApi.campaigns.list();
      setCampaigns(res.data.campaigns || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ name: '', campaign_type: 'email', status: 'draft', target_audience: '', subject_line: '', content: '' }); setEditId(null); setShowForm(true); };

  const openEdit = (c) => {
    setForm({ name: c.name, campaign_type: c.campaign_type, status: c.status, target_audience: c.target_audience || '', subject_line: c.subject_line || '', content: '' });
    setEditId(c.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Campaign name required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await crmApi.campaigns.update(editId, form);
        toast.success('Campaign updated');
      } else {
        await crmApi.campaigns.create(form);
        toast.success('Campaign created');
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await crmApi.campaigns.delete(showDelete);
      toast.success('Campaign deleted');
      setShowDelete(null);
      load();
    } catch (err) { toast.error('Failed to delete'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Campaigns</h1>
          <p className="text-[#64748B] mt-1">Create and manage marketing campaigns</p>
        </div>
        <Button onClick={openCreate} className="careiq-btn-primary" data-testid="create-campaign-btn"><Plus className="w-4 h-4 mr-2" />New Campaign</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
      ) : campaigns.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((c) => {
            const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
            const Icon = cfg.icon;
            const m = c.metrics || {};
            const openRate = m.sent > 0 ? ((m.opened / m.sent) * 100).toFixed(1) : 0;
            const clickRate = m.opened > 0 ? ((m.clicked / m.opened) * 100).toFixed(1) : 0;
            return (
              <Card key={c.id} className="careiq-card hover:border-[#0E7490] transition-colors" data-testid={`campaign-${c.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-[#0A2342]">{c.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1" style={{ backgroundColor: cfg.color + '15', color: cfg.color }}>
                          <Icon className="w-3 h-3" />{cfg.label}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full flex items-center gap-1">
                          {c.campaign_type === 'email' ? <Mail className="w-3 h-3" /> : c.campaign_type === 'landing_page' ? <Globe className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                          {c.campaign_type}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)}><Edit className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowDelete(c.id)}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button>
                    </div>
                  </div>
                  {c.target_audience && <div className="text-xs text-[#64748B]">Target: {c.target_audience}</div>}
                  <div className="grid grid-cols-4 gap-2 p-2 bg-[#F8FAFC] rounded-lg">
                    <div className="text-center"><div className="text-sm font-bold text-[#0A2342]">{m.sent || 0}</div><div className="text-[9px] text-[#64748B]">Sent</div></div>
                    <div className="text-center"><div className="text-sm font-bold text-[#0E7490]">{openRate}%</div><div className="text-[9px] text-[#64748B]">Open</div></div>
                    <div className="text-center"><div className="text-sm font-bold text-[#7C3AED]">{clickRate}%</div><div className="text-[9px] text-[#64748B]">Click</div></div>
                    <div className="text-center"><div className="text-sm font-bold text-[#166534]">{m.converted || 0}</div><div className="text-[9px] text-[#64748B]">Conv.</div></div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="careiq-card"><CardContent className="py-12 text-center text-[#64748B]"><Zap className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" /><p>No campaigns yet</p></CardContent></Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md" data-testid="campaign-form-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Edit Campaign' : 'New Campaign'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Campaign Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.campaign_type} onValueChange={(v) => setForm({ ...form, campaign_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="landing_page">Landing Page</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Target Audience</Label><Input value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} /></div>
            <div><Label>Subject Line</Label><Input value={form.subject_line} onChange={(e) => setForm({ ...form, subject_line: e.target.value })} /></div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-campaign-btn">{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="text-[#991B1B]">Delete Campaign</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] mt-2">Delete this campaign permanently?</p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDelete(null)}>Cancel</Button>
            <Button className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white" onClick={handleDelete} disabled={saving}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
